import React, { useEffect, useState } from "react";
import api, { mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, ArrowLeft } from "lucide-react";
import TournamentWizard from "@/components/tournament/TournamentWizard";
import TournamentManager from "@/components/tournament/TournamentManager";
import { FORMAT_LABEL } from "@/components/tournament/constants";

function Payments() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/admin/payments").then((r) => setItems(r.data.items)).catch(() => {}); }, []);
  const paid = items.filter((i) => i.payment_status === "paid");
  return (
    <div className="card-surface rounded-xl p-4 mt-8" data-testid="admin-payments">
      <div className="flex items-center justify-between mb-3"><h3 className="font-bold text-white">Payments</h3><span className="text-sm text-white/60">{paid.length} paid · ${paid.reduce((s, i) => s + i.amount, 0).toFixed(2)}</span></div>
      <div className="divide-y divide-white/5 text-xs max-h-64 overflow-y-auto">
        {items.map((i) => (
          <div key={i.session_id} className="flex items-center justify-between py-2 gap-3">
            <span className="text-white/70 truncate">{i.email}</span><span className="text-white/40">{new Date(i.created_at).toLocaleString()}</span>
            <span className={`font-bold uppercase ${i.payment_status === "paid" ? "text-green-400" : "text-white/40"}`}>{i.payment_status}</span>
          </div>
        ))}
        {!items.length && <div className="py-4 text-center text-white/40">No payments yet.</div>}
      </div>
    </div>
  );
}

export default function AdminTournaments() {
  const [view, setView] = useState("list");
  const [list, setList] = useState([]);
  const [manageId, setManageId] = useState(null);

  const load = () => api.get("/manage/tournaments").then((r) => setList(r.data.items)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (id) => { if (!window.confirm("Delete this tournament?")) return; try { await api.delete(`/manage/tournaments/${id}`); toast.success("Deleted"); load(); } catch { toast.error("Failed"); } };
  const back = () => { setView("list"); setManageId(null); load(); };

  if (view === "create") return <TournamentWizard price={0} onCancel={back} onCreated={(t) => { toast.success("Tournament created"); setManageId(t.id); setView("manage"); }} />;
  if (view === "manage") return (
    <div>
      <button onClick={back} className="eyebrow text-white/50 hover:text-white inline-flex items-center gap-1.5 mb-5" data-testid="admin-back-to-list"><ArrowLeft size={13} /> All tournaments</button>
      <TournamentManager id={manageId} onDeleted={back} />
    </div>
  );

  return (
    <div>
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-white">Tournament Dashboard</h1>
          <p className="text-sm text-white/50">All tournaments — yours (free) and those created by public accounts.</p>
        </div>
        <button onClick={() => setView("create")} data-testid="create-tournament-btn" className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[hsl(var(--primary))] text-white text-sm font-bold"><Plus size={16} /> Create Tournament</button>
      </div>
      <div className="space-y-3">
        {list.map((t) => (
          <div key={t.id} className="card-surface rounded-xl p-4 flex items-center gap-4" data-testid={`admin-tournament-${t.id}`}>
            <div className="w-16 h-12 rounded-lg overflow-hidden bg-white/5 shrink-0">{t.cover && <img src={mediaUrl(t.cover)} alt="" className="w-full h-full object-cover" />}</div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold uppercase px-2 py-0.5 rounded bg-white/10 text-white/70">{t.status}</span>
                {t.payment_status === "pending" && <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-500/15 text-amber-400">unpaid</span>}
                {t.payment_status === "paid" && <span className="text-xs font-semibold px-2 py-0.5 rounded bg-green-500/15 text-green-400">paid</span>}
                {t.registration_open && <span className="text-xs text-green-400">● registration open</span>}
              </div>
              <div className="font-bold text-white mt-1 truncate">{t.name}</div>
              <div className="text-xs text-white/40">{t.game} · {FORMAT_LABEL[t.format]} · {t.participant_count} teams{t.owner_name ? ` · by ${t.owner_name}` : ""}</div>
            </div>
            <button onClick={() => { setManageId(t.id); setView("manage"); }} data-testid={`manage-${t.id}`} className="px-3 py-1.5 rounded-lg bg-white/10 text-white text-sm font-semibold hover:bg-white/20">Manage</button>
            <button onClick={() => remove(t.id)} className="p-2 rounded-md text-white/50 hover:text-red-400 hover:bg-white/5" data-testid={`delete-${t.id}`}><Trash2 size={16} /></button>
          </div>
        ))}
        {list.length === 0 && <div className="card-surface rounded-xl p-10 text-center text-white/40">No tournaments yet. Create your first one.</div>}
      </div>
      <Payments />
    </div>
  );
}
